import { useState } from 'react'
import { Wordmark } from '../components/Wordmark'
import { acceptTerms } from '../api'

/**
 * The terms box, on the code sign-in and on the page below: the app's words,
 * with "Read the terms" only when the server names a page.
 *
 * There used to be a fallback, /terms on the marketing site, and that address
 * serves the marketing home page with its prices. Somebody asked to read the
 * terms was sent to a price list, from a page the app's sign-in links to. The
 * server names a terms page only when one is configured, so with none
 * configured this links nothing rather than the wrong page.
 */
export function TermsBox({
  termsUrl,
  checked,
  onChange,
}: {
  termsUrl?: string
  checked: boolean
  onChange: (accepted: boolean) => void
}) {
  return (
    <label className="signin-terms">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>
        I agree to the docsdone terms.
        {termsUrl && (
          <>
            {' '}
            <a href={termsUrl} target="_blank" rel="noopener noreferrer">
              Read the terms
            </a>
          </>
        )}
      </span>
    </label>
  )
}

/**
 * Shown after a federated sign-in when the identity has not yet accepted the
 * terms. Email sign-in collects acceptance on the code step instead.
 *
 * Somebody who will not accept must be able to leave. The page used to offer
 * nothing but Continue, so the only way out was closing the tab, and the
 * session it was holding stayed signed in behind it.
 */
export function AcceptTerms({
  termsUrl,
  onAccepted,
  onSignOut,
}: {
  termsUrl?: string
  onAccepted: () => void
  onSignOut: () => Promise<void>
}) {
  const [accepted, setAccepted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await acceptTerms()
      onAccepted()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record acceptance.')
    } finally {
      setBusy(false)
    }
  }

  async function leave() {
    setLeaving(true)
    setError('')
    try {
      await onSignOut()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign you out.')
    } finally {
      setLeaving(false)
    }
  }

  return (
    <div className="signin-wrap">
      <div className="card signin-card">
        <div className="signin-logo">
          <Wordmark />
        </div>
        <h1>Terms and conditions</h1>
        <p className="small muted">
          {termsUrl
            ? 'Before you continue, read and accept the docsdone terms.'
            : 'Before you continue, accept the docsdone terms.'}
        </p>
        <form onSubmit={submit}>
          <TermsBox termsUrl={termsUrl} checked={accepted} onChange={setAccepted} />
          {error && <div className="notice notice-bad signin-notice">{error}</div>}
          <button className="btn btn-block signin-submit" type="submit" disabled={busy || leaving || !accepted}>
            {busy ? <><span className="spinner" /> Continuing…</> : 'Continue'}
          </button>
        </form>
        {/* Full width and quiet: the way out, under the way on, and big
            enough to hit on a phone. */}
        <div className="signin-back">
          <button
            id="terms-sign-out"
            className="btn btn-ghost btn-block"
            type="button"
            disabled={busy || leaving}
            onClick={() => void leave()}
          >
            {leaving ? <><span className="spinner" /> Signing out…</> : 'Sign out'}
          </button>
        </div>
      </div>
    </div>
  )
}
